/**
 * Social Proof badge — storefront helper.
 *
 * Responsibilities:
 *   1. View beacon — POST /api/social-proof/view on PDP load. The response
 *      carries the badge payload; render it into [data-social-proof].
 *   2. Badge stamp — remember which badge (if any) this visitor saw per
 *      product, so cart adds can be tagged with it (6h TTL).
 *   3. /cart/add interceptor — POST /api/social-proof/cart-add for EVERY
 *      successful add, any product, any page. This feeds the counts the
 *      badge displays, so sitewide coverage is what keeps the numbers honest.
 *
 * No dependencies. Safe to load on any page. Fails silent: the page never
 * breaks or logs noise if Product Tools is unreachable.
 *
 * Coexists with daily-deal.js: both wrap window.fetch and pass through the
 * original result untouched, so the wrappers chain safely in either load
 * order. Guard flags differ (window.__spCartHooked here vs. the private
 * CartAddTracker.initialized property there), so neither disables the other.
 */

(function () {
  'use strict';

  var CONFIG = {
    apiUrl: 'https://product-tools.sanctionsnow.com',
    // apiUrl: 'http://localhost:3000', // Dev
    stampKey: 'sp.stamps.v1',
    visitorKey: 'sp.visitor.v1',
    stampTtlMs: 6 * 60 * 60 * 1000,
  };

  function getVisitorId() {
    try {
      var v = localStorage.getItem(CONFIG.visitorKey);
      if (!v) {
        v = 'v_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
        localStorage.setItem(CONFIG.visitorKey, v);
      }
      return v;
    } catch (_) {
      return null;
    }
  }

  // --- Badge stamps: { [productId]: { metric, ts } } in sessionStorage -----
  function readStamps() {
    try {
      var raw = sessionStorage.getItem(CONFIG.stampKey);
      var stamps = raw ? JSON.parse(raw) : {};
      var now = Date.now();
      var fresh = {};
      for (var pid in stamps) {
        if (stamps[pid] && (now - stamps[pid].ts) < CONFIG.stampTtlMs) fresh[pid] = stamps[pid];
      }
      return fresh;
    } catch (_) {
      return {};
    }
  }

  function writeStamp(productId, metric) {
    try {
      var stamps = readStamps();
      stamps[productId] = { metric: metric, ts: Date.now() };
      sessionStorage.setItem(CONFIG.stampKey, JSON.stringify(stamps));
    } catch (_) { /* storage unavailable, badge still renders */ }
  }

  // --- Badge rendering ------------------------------------------------------
  var ICONS = {
    flame: '<svg class="social-proof-badge__icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M13.5.67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l.03-.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5.67zM11.71 19c-1.78 0-3.22-1.4-3.22-3.14 0-1.62 1.05-2.76 2.81-3.12 1.77-.36 3.6-1.21 4.62-2.58.39 1.29.59 2.65.59 4.04 0 2.65-2.15 4.8-4.8 4.8z"/></svg>',
    eye: '<svg class="social-proof-badge__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg>',
  };

  // No em dashes in copy. Count + noun are bold; the rest reads quietly.
  function badgeHtml(metric, count) {
    var n = Number(count).toLocaleString('en-US');
    if (metric === 'atc_today') {
      return ICONS.flame + '<span><strong>' + n + ' shoppers</strong> added this to their cart today</span>';
    }
    if (metric === 'atc_week') {
      return ICONS.flame + '<span><strong>' + n + ' shoppers</strong> added this to their cart this week</span>';
    }
    if (metric === 'views_today') {
      return ICONS.eye + '<span><strong>' + n + ' people</strong> viewed this today</span>';
    }
    return null;
  }

  function renderBadge(container, badge) {
    var html = badgeHtml(badge.metric, badge.count);
    if (!html) return;
    var el = document.createElement('div');
    el.className = 'social-proof-badge';
    el.setAttribute('role', 'note');
    el.innerHTML = html;
    container.appendChild(el);
    // Next frame so the entrance transition runs.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { el.classList.add('is-visible'); });
    });
  }

  // --- View beacon ----------------------------------------------------------
  function fireViewBeacon() {
    var container = document.querySelector('[data-social-proof]');
    if (!container) return;
    var productId = container.getAttribute('data-product-id');
    if (!productId) return;

    fetch(CONFIG.apiUrl + '/api/social-proof/view', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        productId: productId,
        visitorId: getVisitorId(),
        productTitle: container.getAttribute('data-product-title') || null,
        productHandle: container.getAttribute('data-product-handle') || null,
      }),
    })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) {
        if (data && data.badge) {
          renderBadge(container, data.badge);
          writeStamp(productId, data.badge.metric);
        }
      })
      .catch(function (_) { /* silent, badge simply absent */ });
  }

  // --- /cart/add interceptor ------------------------------------------------
  // Mirrors the proven pattern in daily-deal.js (CartAddTracker.init). Both
  // files wrap window.fetch and return the original promise untouched, so
  // the wrappers chain safely regardless of load order.
  function installCartInterceptor() {
    if (window.__spCartHooked) return;
    window.__spCartHooked = true;

    var origFetch = window.fetch;
    window.fetch = function () {
      var args = arguments;
      var url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
      var result = origFetch.apply(this, args);
      if (url && url.indexOf('/cart/add') !== -1) {
        Promise.resolve(result).then(function (res) {
          if (!res || !res.ok) return;
          var cloned;
          try { cloned = res.clone(); } catch (_) { return; }
          cloned.json().then(reportCartAdd, function (_) { /* non-JSON */ }).catch(function (_) {});
        }, function (_) { /* fetch failed */ });
      }
      return result;
    };
  }

  function reportCartAdd(payload) {
    // /cart/add.js returns either a single line item or { items: [...] }.
    // Same detection daily-deal.js uses: items array first, else variant_id.
    var items;
    if (payload && Array.isArray(payload.items) && payload.items.length > 0) {
      items = payload.items;
    } else if (payload && payload.variant_id) {
      items = [payload];
    } else {
      return;
    }

    fetch('/cart.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        // "<token>?key=<hex>" from /cart.js vs bare token in the orders
        // webhook: strip so attribution can match (migration 086 lesson).
        var cartToken = cart && cart.token ? String(cart.token).split('?')[0] : null;
        if (!cartToken) return;
        var stamps = readStamps();
        var visitorId = getVisitorId();

        items.forEach(function (item) {
          var productId = String(item.product_id || '');
          if (!productId) return;
          var stamp = stamps[productId];
          fetch(CONFIG.apiUrl + '/api/social-proof/cart-add', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              productId: productId,
              variantId: String(item.variant_id || item.id || ''),
              cartToken: cartToken,
              visitorId: visitorId,
              price: typeof item.price === 'number' ? item.price / 100 : null,
              quantity: item.quantity || 1,
              badgeMetric: stamp ? stamp.metric : null,
            }),
          }).catch(function (_) {});
        });
      })
      .catch(function (_) {});
  }

  // --- Init -----------------------------------------------------------------
  installCartInterceptor();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fireViewBeacon);
  } else {
    fireViewBeacon();
  }
})();
